import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert
} from '@mui/material';
import {
  WifiOff as OfflineIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Cancel as UnavailableIcon
} from '@mui/icons-material';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import offlineStorage from '../utils/offlineStorage';

/**
 * Offline Page
 * Displayed when the user navigates to a page while offline
 */
const Offline = () => {
  const { isOnline } = useOnlineStatus();
  const [cachedData, setCachedData] = useState({
    sales: 0,
    inventory: 0,
    leads: 0
  });
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    loadCachedData();
  }, []);

  const loadCachedData = async () => {
    try {
      const [sales, inventory, leads, syncTime] = await Promise.all([
        offlineStorage.sales.getAll(),
        offlineStorage.inventory.getAll(),
        offlineStorage.leads.getAll(),
        offlineStorage.getLastSync()
      ]);

      setCachedData({
        sales: sales.length,
        inventory: inventory.length,
        leads: leads.length
      });

      setLastSync(syncTime);
    } catch (error) {
      console.error('Failed to load cached data:', error);
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const availableFeatures = [
    {
      name: 'View Cached Sales',
      available: cachedData.sales > 0,
      description: `${cachedData.sales} sales available offline`
    },
    {
      name: 'View Cached Inventory',
      available: cachedData.inventory > 0,
      description: `${cachedData.inventory} items available offline`
    },
    {
      name: 'View Cached Leads',
      available: cachedData.leads > 0,
      description: `${cachedData.leads} leads available offline`
    },
    {
      name: 'Basic Navigation',
      available: true,
      description: 'Navigate between cached pages'
    }
  ];

  const unavailableFeatures = [
    'Create or edit data',
    'Real-time updates',
    'Search across all records',
    'Upload images',
    'Send emails'
  ];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          border: (theme) => `1px solid ${theme.palette.divider}`
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 3
          }}
        >
          <OfflineIcon
            sx={{
              fontSize: 80,
              color: 'warning.main',
              mb: 2
            }}
          />
          <Typography variant="h4" gutterBottom>
            You're Offline
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            It looks like you've lost your internet connection. Some features are limited while offline.
          </Typography>

          {isOnline && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Connection restored! Click retry to reload the page.
            </Alert>
          )}

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRetry}
            size="large"
            sx={{ mt: 2 }}
          >
            Retry Connection
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'left', mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Available Features
          </Typography>
          <List dense>
            {availableFeatures.map((feature, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  {feature.available ? (
                    <CheckIcon color="success" />
                  ) : (
                    <UnavailableIcon color="disabled" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={feature.name}
                  secondary={feature.description}
                  primaryTypographyProps={{
                    color: feature.available ? 'text.primary' : 'text.disabled'
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'left', mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Unavailable While Offline
          </Typography>
          <List dense>
            {unavailableFeatures.map((feature, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <UnavailableIcon color="disabled" />
                </ListItemIcon>
                <ListItemText
                  primary={feature}
                  primaryTypographyProps={{
                    color: 'text.disabled'
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {lastSync && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Last synced: {new Date(lastSync).toLocaleString()}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Changes you make while offline will be saved and synced automatically when you reconnect.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Offline;
